import { gql } from '@apollo/client';

// Bienestar Corporativo — check-in semanal + Índice OTRIX + tablero RRHH.
// La k-anonymity vive en el backend: aquí solo se pintan `protected`/scores nulos.

// Cadencia de 3 check-ins por semana (lunes/miércoles/viernes = slots 1/2/3) con UNA pregunta
// cada uno. El backend devuelve solo los slots que YA abrieron y siguen sin responder, así que
// quien entra el viernes recibe los tres juntos y quien entra el lunes recibe uno solo. Lo no
// respondido muere el domingo a medianoche: no se arrastra a la semana siguiente.
export const PENDING_WELLBEING_CHECKIN = gql`
  query pendingWellbeingCheckin {
    pendingWellbeingCheckin {
      week_key
      eligible
      completed
      slots {
        slot
        question {
          id
          code
          dimension
        }
      }
    }
  }
`;

export const SUBMIT_WELLBEING_CHECKIN = gql`
  mutation submitWellbeingCheckin($answers: [WellbeingAnswerInput!]!) {
    submitWellbeingCheckin(answers: $answers) {
      week_key
      recommendations
    }
  }
`;

export const MY_WELLBEING_SUMMARY = gql`
  query myWellbeingSummary($weeks: Int) {
    myWellbeingSummary(weeks: $weeks) {
      current_score
      dimensions {
        dimension
        score
        protected
      }
      points {
        week_key
        score
        protected
        respondents
      }
    }
  }
`;

export const ORGANIZATION_WELLBEING = gql`
  query organizationWellbeing($organization_id: ID!, $department_id: ID, $weeks: Int) {
    organizationWellbeing(organization_id: $organization_id, department_id: $department_id, weeks: $weeks) {
      score
      protected
      respondents
      members
      participation_percent
      dimensions {
        dimension
        score
        protected
      }
      trend {
        week_key
        score
        protected
        respondents
      }
    }
  }
`;

export const WELLBEING_HEATMAP = gql`
  query wellbeingHeatmap($organization_id: ID!, $weeks: Int) {
    wellbeingHeatmap(organization_id: $organization_id, weeks: $weeks) {
      department_id
      department_name
      members
      respondents
      participation_percent
      protected
      overall
      cells {
        dimension
        score
        protected
      }
    }
  }
`;

export const WELLBEING_PARTICIPATION = gql`
  query wellbeingParticipation($organization_id: ID!, $weeks: Int) {
    wellbeingParticipation(organization_id: $organization_id, weeks: $weeks) {
      week_key
      respondents
      members
      percent
    }
  }
`;

export const WELLBEING_ALERTS = gql`
  query wellbeingAlerts($organization_id: ID!) {
    wellbeingAlerts(organization_id: $organization_id) {
      id
      department_id
      department_name
      dimension
      week_key
      previous_score
      current_score
      delta
      created_at
    }
  }
`;

export const WELLBEING_REPORT_URL = gql`
  query organizationWellbeingReportUrl($organization_id: ID!, $weeks: Int) {
    organizationWellbeingReportUrl(organization_id: $organization_id, weeks: $weeks)
  }
`;

/* --------------------------- Adopción por departamento --------------------------- */

// Detalle para el panel lateral: agregados + personas. Aquí SÍ hay datos por persona, pero
// solo de USO de la app (nunca bienestar): esa separación la garantiza el backend.
export const ORGANIZATION_DEPARTMENT_DETAIL = gql`
  query organizationDepartmentDetail($organization_id: ID!, $department_id: ID, $weeks: Int) {
    organizationDepartmentDetail(organization_id: $organization_id, department_id: $department_id, weeks: $weeks) {
      department_id
      department_name
      members
      active_members
      active_last_week
      adoption_percent
      weekly_adoption_percent
      workouts
      minutes
      workouts_per_member
      avg_completion_percent
      weeks
      people {
        user_id
        full_name
        photo
        plan_name
        last_seen_at
        workouts
        minutes
        avg_completion_percent
        recovery_sessions
        results
        is_active
      }
    }
  }
`;

// La MISMA consulta sin `people`: es la que alimenta el Excel, donde no puede salir ningún
// nombre. Pedir menos campos es la forma más barata de que ese requisito no se pueda romper.
export const ORGANIZATION_DEPARTMENT_ADOPTION = gql`
  query organizationDepartmentAdoption($organization_id: ID!, $department_id: ID, $weeks: Int) {
    organizationDepartmentDetail(organization_id: $organization_id, department_id: $department_id, weeks: $weeks) {
      department_id
      department_name
      members
      active_members
      active_last_week
      adoption_percent
      weekly_adoption_percent
      workouts
      minutes
      workouts_per_member
      avg_completion_percent
      weeks
    }
  }
`;

export const WELLBEING_REPORTS = gql`
  query wellbeingReports($organization_id: ID!) {
    wellbeingReports(organization_id: $organization_id) {
      id
      trigger
      format
      weeks
      week_key
      score
      members
      respondents
      participation_percent
      was_protected
      generated_by_name
      created_at
    }
  }
`;

export const RESET_WELLBEING_CHECKIN = gql`
  mutation resetWellbeingCheckin($organization_id: ID!, $user_ids: [ID!]) {
    resetWellbeingCheckin(organization_id: $organization_id, user_ids: $user_ids)
  }
`;

/* ------------------------------- Departamentos ------------------------------- */

export const ORGANIZATION_DEPARTMENTS = gql`
  query organizationDepartments($id: ID!) {
    organization(id: $id) {
      id
      departments {
        id
        name
        is_active
        members_count
      }
    }
  }
`;

export const CREATE_ORGANIZATION_DEPARTMENT = gql`
  mutation createOrganizationDepartment($organization_id: ID!, $name: String!) {
    createOrganizationDepartment(organization_id: $organization_id, name: $name) {
      id
      name
      is_active
      members_count
    }
  }
`;

export const UPDATE_ORGANIZATION_DEPARTMENT = gql`
  mutation updateOrganizationDepartment($id: ID!, $name: String, $is_active: Boolean) {
    updateOrganizationDepartment(id: $id, name: $name, is_active: $is_active) {
      id
      name
      is_active
      members_count
    }
  }
`;

export const DELETE_ORGANIZATION_DEPARTMENTS = gql`
  mutation deleteOrganizationDepartments($id: [ID!]!) {
    deleteOrganizationDepartments(id: $id) {
      id
    }
  }
`;

export const SET_USERS_DEPARTMENT = gql`
  mutation setUsersDepartment($organization_id: ID!, $user_ids: [ID!]!, $department_id: ID) {
    setUsersDepartment(organization_id: $organization_id, user_ids: $user_ids, department_id: $department_id)
  }
`;
